import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, IndianRupee, Home, Droplets, Zap, Bath, Clock, Lock, Phone, MessageCircle, Navigation, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DiyaLoader, Toranam } from "@/components/Ornaments";
import PaymentModal from "@/components/PaymentModal";
import { toast } from "sonner";

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 text-sm"><Icon className="w-4 h-4 text-saffron shrink-0" /><span className="text-muted-foreground font-telugu">{label}:</span><span className="text-maroon font-medium font-telugu">{value}</span></div>
);

export default function TempleDetail() {
  const { id } = useParams();
  const { t, settings } = useApp();
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [tm, setTm] = useState(null);
  const [pay, setPay] = useState(false);

  const load = async () => { try { const { data } = await api.get(`/temples/${id}`); setTm(data); } catch (e) { toast.error("దేవాలయం కనబడలేదు"); } };
  useEffect(() => { load(); }, [id]);

  const unlock = async () => {
    if (!user) return nav(`/auth?mode=login&next=/temples/${id}`);
    if (user.role !== "archaka") return toast.info("ఈ సభ్యత్వం అర్చకులకు మాత్రమే. అర్చకుల ఖాతాతో లాగిన్ అవ్వండి.");
    setPay(true);
  };

  if (!tm) return <div className="parchment-texture min-h-screen"><DiyaLoader /></div>;
  const prefMap = { family: `👨‍👩‍👦 ${t("family_preferred")}`, bachelor: `🧘 ${t("bachelor_preferred")}`, none: `🕉️ ${t("no_pref")}` };
  const puroMap = { allowed: t("pref_allowed"), not_allowed: t("pref_not_allowed"), after_hours: t("pref_after") };

  return (
    <div className="parchment-texture min-h-screen pb-10">
      <div className="relative h-64 bg-gradient-to-br from-maroon to-saffron">
        {tm.temple_photos?.length ? <img src={tm.temple_photos[0]} alt="" className="w-full h-full object-cover opacity-90" /> : <div className="w-full h-full flex items-center justify-center text-8xl">🛕</div>}
        <div className="absolute inset-0 bg-gradient-to-t from-maroon/80 to-transparent" />
        <div className="absolute bottom-4 left-0 right-0 max-w-5xl mx-auto px-6">
          <Badge className="mb-2 lineage-badge font-telugu">{prefMap[tm.archaka_preference]}</Badge>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-white">{tm.name}</h1>
          <p className="text-marigold font-telugu">{tm.deity}</p>
        </div>
      </div>
      <Toranam />

      <div className="max-w-5xl mx-auto px-6 py-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-surface rounded-2xl gold-border temple-shadow p-6 space-y-2">
            <h3 className="font-serifte font-bold text-maroon text-lg mb-2">📍 స్థల వివరాలు</h3>
            <Row icon={MapPin} label={t("village")} value={`${tm.village}, ${tm.mandal}`} />
            <Row icon={MapPin} label={t("district")} value={`${tm.district}, ${tm.state}`} />
          </div>

          <div className="bg-surface rounded-2xl gold-border temple-shadow p-6">
            <h3 className="font-serifte font-bold text-maroon text-lg mb-3">💰 వేతనం & సౌకర్యాలు</h3>
            <div className="flex flex-wrap gap-2">
              <Badge className="bg-marigold/20 text-maroon border-goldline/40 font-telugu"><IndianRupee className="w-3 h-3 mr-0.5" />{(tm.salary || 0).toLocaleString("en-IN")} {t("per_month")}</Badge>
              {tm.ddn && <Badge className="bg-marigold/20 text-maroon border-goldline/40 font-telugu">🪔 {t("ddn")}</Badge>}
              {tm.free_food && <Badge className="bg-marigold/20 text-maroon border-goldline/40 font-telugu">🍲 {t("free_food")}</Badge>}
            </div>
          </div>

          <div className="bg-surface rounded-2xl gold-border temple-shadow p-6 space-y-2">
            <h3 className="font-serifte font-bold text-maroon text-lg mb-2"><Clock className="w-5 h-5 inline mr-1 text-saffron" />{t("duty_timings")}</h3>
            {tm.morning_shift && <Row icon={Clock} label={t("morning")} value={tm.morning_shift} />}
            {tm.evening_shift && <Row icon={Clock} label={t("evening")} value={tm.evening_shift} />}
            {tm.afternoon_note && <p className="text-sm text-muted-foreground font-telugu">☀️ {tm.afternoon_note}</p>}
            {tm.festival_note && <p className="text-sm text-muted-foreground font-telugu">🎉 {tm.festival_note}</p>}
          </div>

          {tm.accommodation && (
            <div className="bg-surface rounded-2xl gold-border temple-shadow p-6">
              <h3 className="font-serifte font-bold text-maroon text-lg mb-3">🏠 {t("quarters")}</h3>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="border-goldline/50 text-maroon font-telugu"><Home className="w-3 h-3 mr-1" />{tm.house_type}</Badge>
                {tm.water && <Badge variant="outline" className="border-goldline/50 text-maroon font-telugu"><Droplets className="w-3 h-3 mr-1" />నీరు</Badge>}
                {tm.power_backup && <Badge variant="outline" className="border-goldline/50 text-maroon font-telugu"><Zap className="w-3 h-3 mr-1" />పవర్ బ్యాకప్</Badge>}
                {tm.pooja_room && <Badge variant="outline" className="border-goldline/50 text-maroon font-telugu">🕉️ పూజ గది</Badge>}
                {tm.attached_restroom && <Badge variant="outline" className="border-goldline/50 text-maroon font-telugu"><Bath className="w-3 h-3 mr-1" />అటాచ్డ్ బాత్</Badge>}
              </div>
              {tm.quarters_photos?.length > 0 && <div className="mt-3 flex gap-2 flex-wrap">{tm.quarters_photos.map((p, i) => <img key={i} src={p} alt="" className="w-24 h-24 rounded-lg object-cover gold-border" />)}</div>}
            </div>
          )}

          {tm.description && <div className="bg-surface rounded-2xl gold-border temple-shadow p-6"><h3 className="font-serifte font-bold text-maroon text-lg mb-2">📜 వివరణ</h3><p className="text-sm text-muted-foreground font-telugu leading-relaxed">{tm.description}</p></div>}

          <div className="flex flex-wrap gap-2">
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 font-telugu">🙏 గ్రామ పురోహితం: {puroMap[tm.village_purohitam]}</Badge>
          </div>
        </div>

        {/* Contact unlock card */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 bg-surface rounded-2xl gold-border temple-shadow p-6">
            <h3 className="font-serifte font-bold text-maroon text-lg mb-3">📞 సంప్రదింపు</h3>
            {tm.unlocked ? (
              <div className="space-y-3">
                <Row icon={Phone} label="ఫోన్" value={tm.contact_phone} />
                {tm.contact_name && <p className="text-sm font-telugu text-muted-foreground">{tm.contact_name}</p>}
                <a data-testid="temple-call-btn" href={`tel:${tm.contact_phone}`}><Button className="w-full btn-sacred border-0 font-telugu mb-2"><Phone className="w-4 h-4 mr-2" />{t("call_now")}</Button></a>
                <a data-testid="temple-wa-btn" href={`https://wa.me/91${tm.contact_phone}`} target="_blank" rel="noreferrer"><Button className="w-full bg-[#25D366] hover:bg-[#1da851] text-white border-0 font-telugu mb-2"><MessageCircle className="w-4 h-4 mr-2" />WhatsApp</Button></a>
                {tm.map_link && <a href={tm.map_link} target="_blank" rel="noreferrer"><Button variant="outline" className="w-full border-goldline/50 font-telugu"><Navigation className="w-4 h-4 mr-2" />గూగుల్ మ్యాప్స్</Button></a>}
              </div>
            ) : (
              <div className="text-center">
                <div className="p-4 rounded-xl bg-marigold/10 mb-3">
                  <Lock className="w-8 h-8 text-saffron mx-auto mb-2" />
                  <p className="lock-blur text-maroon text-lg">98480 12345</p>
                  <p className="text-xs text-muted-foreground font-telugu mt-2">ఫోన్ నంబర్, చిరునామా (మ్యాప్) అర్చక సభ్యులకు మాత్రమే కనిపిస్తాయి</p>
                </div>
                <Button data-testid="temple-unlock-btn" onClick={unlock} className="w-full h-auto py-2.5 whitespace-normal leading-snug btn-sacred border-0 font-telugu">
                  🔓 ₹{settings?.archaka_monthly_fee || 499}/నెల — అన్ని దేవాలయాల కాంటాక్ట్ చూడండి
                </Button>
                <p className="text-[11px] text-muted-foreground font-telugu mt-2">ఒక్కసారి కడితే 30 రోజులు అన్ని దేవాలయాలు కనిపిస్తాయి</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <PaymentModal open={pay} onClose={() => setPay(false)} itemType="archaka_subscription" onSuccess={() => { refreshUser(); load(); }} />
    </div>
  );
}
